import React, { useMemo, useState } from 'react';
import { Card, CardBody, CardHeader, Collapse, Input, Table } from 'reactstrap';

import Icon, { faChevronDown, faChevronUp, faCircleExclamation } from '~components/icon';
import { TablePagination, TableView } from '~components/table-view';
import { formatDateString } from '~libs/dayjs';

import { dexIssueColumns, dexSessionColumns } from '../forms/dexPreviewColumns';
import { DexPreviewResult } from '../types';

const SESSION_PAGE_SIZE = 50;

interface ComponentProps {
  preview: DexPreviewResult;
  // Rendered between the summary table and the issues card, with both above the sessions card, so
  // exclusions are seen before the session list or a download (TAV-3369). The panel owns the
  // ordering rather than the caller rendering the banner as a sibling, because the issues list is
  // unbounded and a banner placed after the card scrolls away from the detail it explains. Kept as
  // a prop rather than built here so the panel stays presentational.
  issuesBanner?: React.ReactNode;
  'data-cy'?: string;
}

const DexPreviewPanel: React.FC<ComponentProps> = ({ preview, issuesBanner, 'data-cy': dataCy }) => {
  const [issuesOpen, setIssuesOpen] = useState(true);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [sessionSearch, setSessionSearch] = useState('');
  const [sessionPage, setSessionPage] = useState(1);
  // `sessions` is new in the preview response. Default it so a web build served an older API
  // response (deploy-order slip, API-only rollback) hides the table instead of crashing the step.
  const sessions = useMemo(() => preview.sessions ?? [], [preview.sessions]);

  const filteredSessions = useMemo(() => {
    const term = sessionSearch.trim().toLowerCase();
    if (!term) return sessions;
    return sessions.filter((session) =>
      [
        session.client_name,
        String(session.appointment_id),
        session.client_dex_id,
        session.package_name,
        session.service_type_name,
        session.case_id,
      ].some((value) => value?.toLowerCase().includes(term)),
    );
  }, [sessions, sessionSearch]);

  const pagedSessions = useMemo(() => {
    const start = (sessionPage - 1) * SESSION_PAGE_SIZE;
    return filteredSessions.slice(start, start + SESSION_PAGE_SIZE).map((row) => ({ ...row, id: row.appointment_id }));
  }, [filteredSessions, sessionPage]);

  const handleSessionSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSessionSearch(e.target.value);
    setSessionPage(1);
  };

  // Only while the card is open, since paging a collapsed table does nothing.
  const renderSessionsPagination = (position: 'top' | 'bottom') =>
    sessionsOpen &&
    filteredSessions.length > 0 && (
      <div className="d-flex justify-content-end mt-2">
        <div className="dex-inline-pagination" data-cy={`dex-preview-sessions-pagination-${position}`}>
          <TablePagination
            showCount={false}
            currentPage={sessionPage}
            totalPages={Math.ceil(filteredSessions.length / SESSION_PAGE_SIZE)}
            pageSize={SESSION_PAGE_SIZE}
            totalCount={filteredSessions.length}
            onPageChange={setSessionPage}
          />
        </div>
      </div>
    );

  return (
    <div className="dex-preview-panel" data-cy={dataCy ?? 'dex-preview-panel'}>
      <Table bordered size="sm" style={{ maxWidth: '400px' }}>
        <tbody>
          <tr>
            <th scope="row">Date range</th>
            <td data-cy="dex-preview-date-range">
              {formatDateString(preview.date_from)} – {formatDateString(preview.date_to)}
            </td>
          </tr>
          <tr>
            <th scope="row">Clients</th>
            <td data-cy="dex-preview-client-count">{preview.client_count}</td>
          </tr>
          <tr>
            <th scope="row">Sessions</th>
            <td data-cy="dex-preview-session-count">{preview.session_count}</td>
          </tr>
        </tbody>
      </Table>

      {issuesBanner}

      {preview.issues.length > 0 && (
        <Card className="rounded-0 mt-2" data-cy="dex-preview-issues-card">
          <CardHeader
            className="d-flex justify-content-between border-0 fw-bold align-items-center"
            tag="button"
            type="button"
            onClick={() => setIssuesOpen((prev) => !prev)}
            aria-expanded={issuesOpen}
            aria-controls="dex-preview-issues-table"
            data-cy="dex-preview-issues-toggle"
          >
            <div>
              <Icon icon={faCircleExclamation} className="text-warning me-2" />
              Data quality issues ({preview.issues.length})
            </div>
            <Icon icon={issuesOpen ? faChevronUp : faChevronDown} className="ms-2" />
          </CardHeader>
          <Collapse isOpen={issuesOpen}>
            <CardBody className="p-0">
              <TableView
                id="dex-preview-issues-table"
                columns={dexIssueColumns}
                data={preview.issues.map((issue, index) => ({ ...issue, id: `${issue.appointment_id}-${index}` }))}
                emptyMessage="No issues"
                readonly
                striped
              />
            </CardBody>
          </Collapse>
        </Card>
      )}

      {/* Page numbers sit above and below the sessions card, far right (same as DEX Readiness). */}
      {renderSessionsPagination('top')}
      {sessions.length > 0 && (
        <Card className="rounded-0 mt-2" data-cy="dex-preview-sessions-card">
          <CardHeader
            className="d-flex justify-content-between border-0 fw-bold align-items-center"
            tag="button"
            type="button"
            onClick={() => setSessionsOpen((prev) => !prev)}
            aria-expanded={sessionsOpen}
            aria-controls="dex-preview-sessions-body"
            data-cy="dex-preview-sessions-toggle"
          >
            <div>Sessions in export ({sessions.length})</div>
            <Icon icon={sessionsOpen ? faChevronUp : faChevronDown} className="ms-2" />
          </CardHeader>
          <Collapse isOpen={sessionsOpen}>
            <CardBody className="p-0" id="dex-preview-sessions-body">
              <div className="p-2">
                <Input
                  type="text"
                  bsSize="sm"
                  placeholder="Search client, appointment, package or case ID"
                  aria-label="Search sessions"
                  data-cy="dex-preview-sessions-search"
                  value={sessionSearch}
                  onChange={handleSessionSearchChange}
                />
              </div>
              <TableView
                id="dex-preview-sessions-table"
                columns={dexSessionColumns}
                data={pagedSessions}
                emptyMessage="No sessions match your search"
                readonly
                striped
              />
            </CardBody>
          </Collapse>
        </Card>
      )}
      {renderSessionsPagination('bottom')}
    </div>
  );
};

export default DexPreviewPanel;
